import { GEOSPACE_CHIBAN_TILE_LAYER_NAME } from "@/features/map/components/CommonChibanTileLayer";
import { MUNICIPAL_DOCUMENT_LAYER_NAME } from "@/features/map/components/layers/MunicipalDocumentLayer";
import { REQUESTED_PICTURES_PLOT_LAYER_NAME } from "@/features/map/components/layers/RequestedPicturesPlotLayer";

/**
 * ゼンリン契約者向けに表示する地番地図レイヤー名（ブルーマップ）。
 * 既存実装では定数化されていないため、URL パラメータ機能で参照する分のみここに集約する。
 */
export const BLUE_MAP_LAYER_NAME = "ブルーマップ";

/**
 * ゼンリン契約者向けに表示する住宅地図レイヤー名（表札なし）。
 * `BLUE_MAP_LAYER_NAME` と合わせ、GEOSPACE 優先排他で除外するゼンリン系レイヤーを表す。
 */
export const ZENRIN_RESIDENTIAL_LAYER_NAME = "住宅地図（表札なし）";

/**
 * `chibanchizu=true` 指定時のレイヤー配列変換（純粋関数）。
 *
 * chibanchizu レイヤー切り替えの単一の業務ルール。
 * 現状の呼び出し元は `computeUrlBasedCheckedLayers`（同期初期化経路）のみ。
 *
 * - GEOSPACE 契約あり: ゼンリン系（住宅地図(表札なし)・ブルーマップ）を除外し
 *   GEOSPACE 地番タイルを追加（GEOSPACE 優先排他）。
 * - ゼンリン契約のみ: ブルーマップを追加。
 * - 両契約なし: 何もしない。
 *
 * 変更が不要な場合は引数 `layers` を同一参照で返し、不要な再レンダーを避ける。
 */
export const applyChibanchizuLayers = (
  layers: string[],
  hasZenrinContract: boolean,
  hasGeospaceContract: boolean
): string[] => {
  if (hasGeospaceContract) {
    // Step1: ゼンリン系（住宅地図(表札なし)・ブルーマップ）を除外する
    const withoutZenrin = layers.filter(
      (layer) =>
        layer !== BLUE_MAP_LAYER_NAME && layer !== ZENRIN_RESIDENTIAL_LAYER_NAME
    );
    const removedZenrin = withoutZenrin.length !== layers.length;
    const hasGeospaceTile = withoutZenrin.includes(
      GEOSPACE_CHIBAN_TILE_LAYER_NAME
    );
    // ゼンリン系がなく地番タイルも既存なら変更不要 → 同一参照を返し再レンダーを抑制
    if (!removedZenrin && hasGeospaceTile) return layers;
    // Step2: GEOSPACE 地番タイルがなければ末尾に追加する
    return hasGeospaceTile
      ? withoutZenrin
      : [...withoutZenrin, GEOSPACE_CHIBAN_TILE_LAYER_NAME];
  }
  if (hasZenrinContract) {
    return layers.includes(BLUE_MAP_LAYER_NAME)
      ? layers
      : [...layers, BLUE_MAP_LAYER_NAME];
  }
  return layers;
};

/**
 * `requestedPicturesList=true` 指定時のレイヤー配列変換（純粋関数）。
 * 既に含まれる場合は同一参照を返す。
 */
export const addRequestedPicturesLayer = (layers: string[]): string[] =>
  layers.includes(REQUESTED_PICTURES_PLOT_LAYER_NAME)
    ? layers
    : [...layers, REQUESTED_PICTURES_PLOT_LAYER_NAME];

/**
 * `municipalDocuments=true` 指定時のレイヤー配列変換（純粋関数）。
 * 既に含まれる場合は同一参照を返す。
 */
export const addMunicipalDocumentsLayer = (layers: string[]): string[] =>
  layers.includes(MUNICIPAL_DOCUMENT_LAYER_NAME)
    ? layers
    : [...layers, MUNICIPAL_DOCUMENT_LAYER_NAME];
